import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const GOLD = '#D4AF37';

// ── Rule types — must stay in sync with models/HouseRule.js RULE_TYPES enum ──
const RULE_TYPES = [
  { value: 'pattern_match',    label: 'Pattern Match',     help: 'Flag when target section CONTAINS this pattern' },
  { value: 'pattern_absent',   label: 'Pattern Absent',    help: 'Flag when target section is MISSING this pattern' },
  { value: 'section_required', label: 'Section Required',  help: 'Flag when this CSI section is missing entirely' },
  { value: 'code_compliance',  label: '🏛️ Code Compliance', help: 'Flag when a target section is missing a required code citation (Title 24, IBC, ASHRAE, etc.)' },
];

const SEVERITY = {
  critical: { icon: '🔴', color: '#dc2626', bg: '#fef2f2' },
  warning:  { icon: '🟡', color: '#d97706', bg: '#fffbeb' },
  info:     { icon: '🔵', color: '#2563eb', bg: '#eff6ff' },
};

// ── Code standards dropdown — common ones for MEP work ────────────────────────
const CODE_STANDARDS = [
  { value: 'CA Title 24',     label: 'California Title 24 (Energy Code)' },
  { value: 'IBC',             label: 'International Building Code (IBC)' },
  { value: 'IMC',             label: 'International Mechanical Code (IMC)' },
  { value: 'IPC',             label: 'International Plumbing Code (IPC)' },
  { value: 'IECC',            label: 'International Energy Conservation Code (IECC)' },
  { value: 'ASHRAE 90.1',     label: 'ASHRAE 90.1 (Energy Efficiency)' },
  { value: 'ASHRAE 62.1',     label: 'ASHRAE 62.1 (Indoor Air Quality)' },
  { value: 'ASHRAE 55',       label: 'ASHRAE 55 (Thermal Comfort)' },
  { value: 'NFPA 13',         label: 'NFPA 13 (Sprinklers)' },
  { value: 'NFPA 70 / NEC',   label: 'NFPA 70 / NEC (National Electrical Code)' },
  { value: 'NFPA 90A',        label: 'NFPA 90A (HVAC)' },
  { value: 'NFPA 101',        label: 'NFPA 101 (Life Safety)' },
  { value: 'UL 555',          label: 'UL 555 (Fire/Smoke Dampers)' },
  { value: 'ASCE 7',          label: 'ASCE 7 (Seismic)' },
  { value: 'OSHPD',           label: 'OSHPD (CA Healthcare Seismic)' },
  { value: 'UPC',             label: 'Uniform Plumbing Code (UPC)' },
  { value: 'CALGreen',        label: 'CALGreen (Title 24 Part 11)' },
  { value: 'OTHER',           label: 'Other / Custom' },
];

// ── Preset code rules — starting templates Mario should validate with engineers ──
// All use ruleType: 'code_compliance'. The pattern is auto-assembled from
// codeStandard + codeSection + compliancePhrase via buildCodePattern() below.
const CODE_PRESETS = [
  {
    discipline: 'Mechanical',
    name: 'California Title 24 — Mechanical Equipment Efficiency',
    description: 'AHU, chiller, and boiler equipment must reference Title 24 Section 140.4 minimum efficiency requirements.',
    target: '23 73 23',
    codeStandard: 'CA Title 24',
    codeSection: 'Section 140.4',
    compliancePhrase: 'minimum efficiency',
    severity: 'warning',
  },
  {
    discipline: 'Mechanical',
    name: 'ASHRAE 90.1 — Energy Efficiency',
    description: 'HVAC sections must reference ASHRAE 90.1 energy efficiency requirements.',
    target: '23 00 00',
    codeStandard: 'ASHRAE 90.1',
    codeSection: '',
    compliancePhrase: '',
    severity: 'warning',
  },
  {
    discipline: 'Mechanical',
    name: 'NFPA 90A — Fire Damper UL Listing',
    description: 'Fire dampers must reference UL 555 listing per NFPA 90A and IBC Section 717.',
    target: '23 33 13',
    codeStandard: 'UL 555',
    codeSection: '',
    compliancePhrase: 'listed',
    severity: 'critical',
  },
  {
    discipline: 'Mechanical',
    name: 'Seismic Bracing per ASCE 7 / IBC',
    description: 'Mechanical seismic restraints must reference ASCE 7 (or OSHPD for CA healthcare).',
    target: '23 05 48',
    codeStandard: 'ASCE 7',
    codeSection: '',
    compliancePhrase: 'seismic',
    severity: 'warning',
  },
  {
    discipline: 'Electrical',
    name: 'NEC Working Space Requirement',
    description: 'Electrical equipment sections must reference NEC working space requirements (Article 110.26).',
    target: '26 05 00',
    codeStandard: 'NFPA 70 / NEC',
    codeSection: '110.26',
    compliancePhrase: 'working space',
    severity: 'warning',
  },
  {
    discipline: 'Electrical',
    name: 'ASHRAE 90.1 — Lighting Power Density',
    description: 'Lighting sections must reference ASHRAE 90.1 lighting power density (LPD) limits.',
    target: '26 51 00',
    codeStandard: 'ASHRAE 90.1',
    codeSection: 'Section 9',
    compliancePhrase: 'lighting power density|LPD',
    severity: 'warning',
  },
  {
    discipline: 'Plumbing',
    name: 'Backflow Prevention per UPC/IPC',
    description: 'Plumbing sections must reference required backflow prevention assemblies.',
    target: '22 11 00',
    codeStandard: 'UPC',
    codeSection: '',
    compliancePhrase: 'backflow preventer|RPZ',
    severity: 'warning',
  },
  {
    discipline: 'Fire Protection',
    name: 'NFPA 13 — Sprinkler System Design',
    description: 'Sprinkler systems must reference NFPA 13 design and listing requirements.',
    target: '21 13 00',
    codeStandard: 'NFPA 13',
    codeSection: '',
    compliancePhrase: 'listed',
    severity: 'critical',
  },
];

// ── Code pattern builder ──────────────────────────────────────────────────────
// Given a code standard + optional section + optional phrase, build a regex
// that matches when ANY of those tokens are referenced in the target section.
// We treat each token as required-by-OR, so the rule fires if NONE of them
// are present (using pattern_absent semantics) — but we evaluate using
// pattern_match, since we want the rule to fire when the citation IS missing.
//
// Important nuance: pattern_match in the existing evaluator fires when the
// pattern is NOT found. So we just construct a regex of the citation tokens
// joined by alternation, and the rule effectively checks "at least one of
// these citations must appear in the target section."
function buildCodePattern(codeStandard, codeSection, compliancePhrase) {
  const tokens = [];
  if (codeStandard && codeStandard !== 'OTHER') {
    // Escape periods (e.g., "ASHRAE 90.1" needs the period escaped in regex)
    tokens.push(codeStandard.replace(/\./g, '\\.'));
  }
  if (codeSection && codeSection.trim()) {
    tokens.push(codeSection.trim().replace(/\./g, '\\.'));
  }
  if (compliancePhrase && compliancePhrase.trim()) {
    // Compliance phrase may already contain regex alternation (e.g., "LPD|lighting power density")
    tokens.push(compliancePhrase.trim());
  }
  if (tokens.length === 0) return '';
  return tokens.join('|');
}

// ── Reverse parser: from a stored pattern, try to reconstruct code fields ────
// Best-effort. If the pattern doesn't parse cleanly into known parts, falls
// back to dumping it into compliancePhrase so the user can edit it manually.
function parseCodePattern(pattern, ruleType) {
  if (ruleType !== 'code_compliance' || !pattern) {
    return { codeStandard: '', codeSection: '', compliancePhrase: '' };
  }
  const tokens = pattern.split('|').map(t => t.replace(/\\\./g, '.').trim());
  let codeStandard = '';
  let codeSection = '';
  const remaining = [];

  for (const tok of tokens) {
    // First token matching a known code standard becomes codeStandard
    if (!codeStandard && CODE_STANDARDS.some(c => c.value === tok)) {
      codeStandard = tok;
    }
    // A token that looks like "Section X" or "Article X" or starts with a digit and a period
    else if (!codeSection && /^(Section\s|Article\s|Chapter\s|\d+\.)/i.test(tok)) {
      codeSection = tok;
    }
    else {
      remaining.push(tok);
    }
  }
  return {
    codeStandard,
    codeSection,
    compliancePhrase: remaining.join('|'),
  };
}

const S = {
  wrap: { display: 'flex', gap: 24, padding: 24 },
  sideNav: { width: 220, flexShrink: 0 },
  navItem: (active) => ({
    padding: '10px 14px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
    background: active ? '#fff8e1' : 'transparent',
    borderLeft: active ? `3px solid ${GOLD}` : '3px solid transparent',
    fontWeight: active ? 600 : 400, fontSize: 14,
  }),
  main: { flex: 1, background: '#fff', borderRadius: 8, padding: 24, border: '1px solid #e5e7eb' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h2: { margin: 0, fontSize: 20, fontWeight: 700 },
  h3: { margin: 0, fontSize: 16, fontWeight: 700, marginBottom: 4 },
  btnPrimary: { background: GOLD, color: '#1a1a1a', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  btnSecondary: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  btnDanger: { background: '#fff', color: '#dc2626', border: '1px solid #fca5a5', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnGhost: { background: 'transparent', color: '#6b7280', border: 'none', padding: '4px 8px', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' },
  ruleCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  badge: (s) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: SEVERITY[s].bg, color: SEVERITY[s].color, marginRight: 8 }),
  empty: { textAlign: 'center', padding: 40, color: '#6b7280' },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: 8, padding: 24, width: 580, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 12, color: '#374151' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  help: { fontSize: 12, color: '#6b7280', marginTop: 4 },
  toggle: (on) => ({ width: 36, height: 20, borderRadius: 10, background: on ? GOLD : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', border: 'none' }),
  toggleDot: (on) => ({ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }),

  // Terminology-specific
  termSection: { marginBottom: 32, paddingBottom: 24, borderBottom: '1px solid #e5e7eb' },
  termSectionLast: { marginBottom: 0, paddingBottom: 0, borderBottom: 'none' },
  termField: { marginBottom: 16, padding: 14, background: '#fafafa', borderRadius: 8, border: '1px solid #e5e7eb' },
  termFieldCustomized: { background: '#fff8e1', borderColor: GOLD },
  termRow: { display: 'flex', alignItems: 'center', gap: 12 },
  termInputWrap: { flex: 1 },
  termCustomBadge: { display: 'inline-block', fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 10, marginLeft: 8 },
  termAlternatives: { fontSize: 11, color: '#9ca3af', marginTop: 6, fontStyle: 'italic' },

  // Preset library
  presetGroup: { marginBottom: 16 },
  presetGroupTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', marginBottom: 6, letterSpacing: '.05em' },
  presetCard: {
    border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 6,
    cursor: 'pointer', transition: 'all .15s',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
  },
  presetCardHover: { borderColor: GOLD, background: '#fffbeb' },
  presetName: { fontSize: 13, fontWeight: 600, color: '#374151' },
  presetMeta: { fontSize: 11, color: '#6b7280', marginTop: 2 },
  presetAdd: { fontSize: 18, color: GOLD, fontWeight: 700 },

  // Code-compliance specific
  codeBox: {
    marginTop: 12, padding: 14, background: '#fef3c7', borderRadius: 6,
    border: '1px solid #fbbf24',
  },
  codePreview: {
    marginTop: 8, padding: 8, background: '#fff', borderRadius: 4,
    border: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: 12, color: '#374151',
  },
};

const EMPTY_RULE = {
  name: '', description: '', ruleType: 'pattern_match',
  target: '', pattern: '', patternIsRegex: false,
  severity: 'warning', enabled: true,
  // Virtual fields for code_compliance UI (not persisted directly):
  codeStandard: '', codeSection: '', compliancePhrase: '',
};

// ── Terminology metadata — matches the backend's DEFAULT_TERMINOLOGY ──────────
const TERMINOLOGY_SECTIONS = [
  {
    scope: 'designVsShop',
    title: 'Design vs. Shop Drawing Recommendations',
    description: 'Customize the recommendation labels shown on PDF reports and in the app for the Design vs. Shop Drawing Review tool.',
    fields: [
      { key: 'noExceptionTaken',     label: 'No Exception Taken (default)',     help: 'Used when the shop drawing fully implements the design intent.', commonAlternatives: ['Approved', 'Approved as Submitted', 'No Exceptions'] },
      { key: 'makeCorrectionsNoted', label: 'Make Corrections Noted (default)', help: 'Used for minor deviations the contractor can correct without resubmittal.', commonAlternatives: ['Approved as Noted', 'Approved with Comments', 'Furnish as Corrected'] },
      { key: 'reviseAndResubmit',    label: 'Revise and Resubmit (default)',    help: 'Used for meaningful deviations requiring formal resubmittal.', commonAlternatives: ['Not Approved — Revise and Resubmit', 'Returned for Correction'] },
      { key: 'rejected',             label: 'Rejected (default)',               help: 'Used when the shop drawing fundamentally fails to implement the design intent.', commonAlternatives: ['Not Approved', 'Not Acceptable', 'Disapproved'] },
    ],
  },
];

export default function HouseRulesSettings({ user }) {
  const userId = user?.email || user?.sub;
  const [section, setSection] = useState('houseRules');

  // ── House Rules state ──
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [presetHover, setPresetHover] = useState(null);

  // ── Terminology state ──
  const [terminology, setTerminology] = useState(null);
  const [termLoading, setTermLoading] = useState(false);
  const [termError, setTermError] = useState(null);
  const [termDirty, setTermDirty] = useState(false);
  const [termSaving, setTermSaving] = useState(false);
  const [termSavedFlash, setTermSavedFlash] = useState(false);

  const loadRules = useCallback(async () => {
    if (!userId) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/house-rules?userId=${encodeURIComponent(userId)}`);
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const data = await r.json();
      setRules(Array.isArray(data) ? data : data.rules || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [userId]);

  const loadTerminology = useCallback(async () => {
    if (!userId) return;
    setTermLoading(true); setTermError(null);
    try {
      const r = await fetch(`${API_BASE}/api/terminology?userId=${encodeURIComponent(userId)}`);
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const data = await r.json();
      setTerminology(data.terminology);
      setTermDirty(false);
    } catch (e) { setTermError(e.message); }
    finally { setTermLoading(false); }
  }, [userId]);

  useEffect(() => { loadRules(); }, [loadRules]);
  useEffect(() => {
    if (section === 'terminology' && !terminology) loadTerminology();
  }, [section, terminology, loadTerminology]);

  // ── Open editor with a preset ──
  const openPreset = (preset) => {
    setEditing({
      ...EMPTY_RULE,
      name: preset.name,
      description: preset.description,
      ruleType: 'code_compliance',
      target: preset.target,
      severity: preset.severity,
      codeStandard: preset.codeStandard,
      codeSection: preset.codeSection,
      compliancePhrase: preset.compliancePhrase,
      // pattern + patternIsRegex assembled at save time from code fields
    });
  };

  // ── Open editor for a new rule (blank) ──
  const openNew = () => setEditing({ ...EMPTY_RULE });

  // ── Open editor for an existing rule ──
  const openEdit = (rule) => {
    const codeFields = parseCodePattern(rule.pattern, rule.ruleType);
    setEditing({
      ...rule,
      codeStandard: codeFields.codeStandard,
      codeSection: codeFields.codeSection,
      compliancePhrase: codeFields.compliancePhrase,
    });
  };

  // ── Save rule (handles both new and existing) ──
  const save = async () => {
    if (!editing.name || !editing.target) { alert('Name and target are required'); return; }

    // For code_compliance rules, assemble the pattern from structured fields
    let payload = { ...editing, userId };
    if (editing.ruleType === 'code_compliance') {
      const assembled = buildCodePattern(editing.codeStandard, editing.codeSection, editing.compliancePhrase);
      if (!assembled) {
        alert('At least one of: Code Standard, Code Section, or Compliance Phrase is required for a Code Compliance rule.');
        return;
      }
      payload.pattern = assembled;
      payload.patternIsRegex = true;
    }
    // Strip virtual fields that don't exist in the schema
    delete payload.codeStandard;
    delete payload.codeSection;
    delete payload.compliancePhrase;

    setSaving(true);
    try {
      const isNew = !editing._id;
      const url = isNew
        ? `${API_BASE}/api/house-rules`
        : `${API_BASE}/api/house-rules/${editing._id}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      setEditing(null);
      await loadRules();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_BASE}/api/house-rules/${rule._id}?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`Delete failed (${r.status})`);
      await loadRules();
    } catch (e) { alert(e.message); }
  };

  const toggleEnabled = async (rule) => {
    try {
      const r = await fetch(`${API_BASE}/api/house-rules/${rule._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rule, enabled: !rule.enabled, userId }),
      });
      if (!r.ok) throw new Error(`Toggle failed (${r.status})`);
      await loadRules();
    } catch (e) { alert(e.message); }
  };

  // ── Terminology editing (unchanged from v2) ──
  const updateTermField = (scope, key, value) => {
    setTerminology(prev => ({ ...prev, [scope]: { ...prev[scope], [key]: { ...prev[scope][key], raw: value } } }));
    setTermDirty(true);
  };
  const resetTermField = (scope, key) => {
    setTerminology(prev => ({ ...prev, [scope]: { ...prev[scope], [key]: { ...prev[scope][key], raw: '' } } }));
    setTermDirty(true);
  };
  const saveTerminology = async () => {
    setTermSaving(true);
    try {
      const payload = {};
      for (const sec of TERMINOLOGY_SECTIONS) {
        payload[sec.scope] = {};
        for (const f of sec.fields) {
          payload[sec.scope][f.key] = terminology[sec.scope]?.[f.key]?.raw || '';
        }
      }
      const r = await fetch(`${API_BASE}/api/terminology`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, terminology: payload }),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      const data = await r.json();
      setTerminology(data.terminology);
      setTermDirty(false);
      setTermSavedFlash(true);
      setTimeout(() => setTermSavedFlash(false), 2200);
    } catch (e) { alert(e.message); }
    finally { setTermSaving(false); }
  };
  const resetAllTerminology = async () => {
    if (!window.confirm('Reset all terminology to AECWorks AI defaults? This will clear all your firm\'s customizations.')) return;
    setTermSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/terminology?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`Reset failed (${r.status})`);
      await loadTerminology();
      setTermSavedFlash(true);
      setTimeout(() => setTermSavedFlash(false), 2200);
    } catch (e) { alert(e.message); }
    finally { setTermSaving(false); }
  };

  // Helpers for editor UI
  const isCodeRule = editing && editing.ruleType === 'code_compliance';
  const showsPattern = editing && (editing.ruleType === 'pattern_match' || editing.ruleType === 'pattern_absent');
  const targetHelp = editing && {
    pattern_match: 'CSI section to scan (e.g., "07 92 00" for joint sealants)',
    pattern_absent: 'CSI section that must contain the pattern (e.g., "08 80 00")',
    section_required: 'CSI section number that must be present (e.g., "09 51 00")',
    code_compliance: 'CSI section that must reference this code (e.g., "23 73 23" for AHUs)',
  }[editing.ruleType];

  // Group presets by discipline for display
  const presetsByDiscipline = CODE_PRESETS.reduce((acc, p) => {
    (acc[p.discipline] = acc[p.discipline] || []).push(p);
    return acc;
  }, {});

  // Live preview of the assembled pattern in the modal
  const livePattern = isCodeRule
    ? buildCodePattern(editing.codeStandard, editing.codeSection, editing.compliancePhrase)
    : '';

  return (
    <div style={S.wrap}>
      <div style={S.sideNav}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', fontWeight: 700, padding: '0 14px 8px' }}>Settings</div>
        <div style={S.navItem(section === 'houseRules')} onClick={() => setSection('houseRules')}>📋 House Rules</div>
        <div style={S.navItem(section === 'terminology')} onClick={() => setSection('terminology')}>🏷️ Terminology</div>
      </div>

      <div style={S.main}>
        {/* ── HOUSE RULES SECTION ── */}
        {section === 'houseRules' && (
          <>
            <div style={S.header}>
              <div>
                <h2 style={S.h2}>House Rules</h2>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Firm-specific compliance checks applied to every spec review.</div>
              </div>
              <button style={S.btnPrimary} onClick={openNew}>+ New Rule</button>
            </div>

            {/* Preset library — only shown when no rules exist OR as a small add-from-template */}
            {!loading && (
              <div style={{ marginBottom: 24, padding: 16, background: '#fffbeb', border: `1px solid ${GOLD}`, borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>🏛️ Common Code Compliance Rules</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Click a preset to add it as a starting template — edit before saving to match your firm's standards.</div>
                  </div>
                </div>

                {Object.entries(presetsByDiscipline).map(([discipline, presets]) => (
                  <div key={discipline} style={S.presetGroup}>
                    <div style={S.presetGroupTitle}>{discipline}</div>
                    {presets.map((preset, i) => {
                      const isHover = presetHover === `${discipline}-${i}`;
                      return (
                        <div
                          key={i}
                          style={{ ...S.presetCard, ...(isHover ? S.presetCardHover : {}) }}
                          onMouseEnter={() => setPresetHover(`${discipline}-${i}`)}
                          onMouseLeave={() => setPresetHover(null)}
                          onClick={() => openPreset(preset)}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={S.presetName}>{preset.name}</div>
                            <div style={S.presetMeta}>
                              {preset.codeStandard} · target: {preset.target} · {SEVERITY[preset.severity].icon} {preset.severity}
                            </div>
                          </div>
                          <span style={S.presetAdd}>+</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {loading && <div style={S.empty}>Loading rules…</div>}
            {error && <div style={{ ...S.empty, color: '#dc2626' }}>{error}</div>}
            {!loading && !error && rules.length === 0 && (
              <div style={S.empty}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>No house rules yet</div>
                <div style={{ fontSize: 13 }}>Add a preset above, or create your first rule from scratch.</div>
              </div>
            )}

            {rules.map((rule) => (
              <div key={rule._id} style={{ ...S.ruleCard, opacity: rule.enabled ? 1 : 0.55 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: 6 }}>
                    <span style={S.badge(rule.severity)}>{SEVERITY[rule.severity].icon} {rule.severity}</span>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{rule.name}</span>
                  </div>
                  {rule.description && <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 6 }}>{rule.description}</div>}
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    <strong>{RULE_TYPES.find(t => t.value === rule.ruleType)?.label || rule.ruleType}</strong> · target: <code>{rule.target}</code>
                    {rule.pattern && <> · pattern: <code>{rule.pattern}</code>{rule.patternIsRegex && ' (regex)'}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button style={S.toggle(rule.enabled)} onClick={() => toggleEnabled(rule)} title={rule.enabled ? 'Disable' : 'Enable'}>
                    <span style={S.toggleDot(rule.enabled)} />
                  </button>
                  <button style={S.btnSecondary} onClick={() => openEdit(rule)}>Edit</button>
                  <button style={S.btnDanger} onClick={() => remove(rule)}>Delete</button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── TERMINOLOGY SECTION (unchanged from v2) ── */}
        {section === 'terminology' && (
          <>
            <div style={S.header}>
              <div>
                <h2 style={S.h2}>Terminology</h2>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Customize the labels AECWorks AI uses in reports to match your firm's standard vocabulary.</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {termSavedFlash && <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>}
                <button style={S.btnSecondary} onClick={resetAllTerminology} disabled={termSaving || termLoading}>Reset to Defaults</button>
                <button style={{ ...S.btnPrimary, opacity: (termSaving || !termDirty) ? 0.55 : 1 }} onClick={saveTerminology} disabled={termSaving || !termDirty}>{termSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>

            {termLoading && <div style={S.empty}>Loading terminology…</div>}
            {termError && <div style={{ ...S.empty, color: '#dc2626' }}>{termError}</div>}

            {!termLoading && !termError && terminology && TERMINOLOGY_SECTIONS.map((sec, secIdx) => (
              <div key={sec.scope} style={secIdx === TERMINOLOGY_SECTIONS.length - 1 ? S.termSectionLast : S.termSection}>
                <h3 style={S.h3}>{sec.title}</h3>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16, lineHeight: 1.5 }}>{sec.description}</div>

                {sec.fields.map(f => {
                  const fieldData = terminology[sec.scope]?.[f.key] || { raw: '', resolved: f.label, default: f.label };
                  const isCustomized = !!fieldData.raw && fieldData.raw.trim() !== '';
                  return (
                    <div key={f.key} style={{ ...S.termField, ...(isCustomized ? S.termFieldCustomized : {}) }}>
                      <label style={{ ...S.label, marginTop: 0, display: 'flex', alignItems: 'center' }}>
                        Default: <span style={{ marginLeft: 6, fontWeight: 400, color: '#6b7280' }}>{fieldData.default}</span>
                        {isCustomized && <span style={S.termCustomBadge}>CUSTOMIZED</span>}
                      </label>
                      <div style={S.termRow}>
                        <div style={S.termInputWrap}>
                          <input
                            style={S.input}
                            value={fieldData.raw || ''}
                            onChange={(e) => updateTermField(sec.scope, f.key, e.target.value)}
                            placeholder={`Leave blank to use "${fieldData.default}"`}
                            maxLength={80}
                          />
                        </div>
                        {isCustomized && <button style={S.btnGhost} onClick={() => resetTermField(sec.scope, f.key)}>Reset</button>}
                      </div>
                      <div style={S.help}>{f.help}</div>
                      {f.commonAlternatives && f.commonAlternatives.length > 0 && (
                        <div style={S.termAlternatives}>Common alternatives: {f.commonAlternatives.join(' · ')}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {!termLoading && !termError && terminology && termDirty && (
              <div style={{ marginTop: 20, padding: 12, background: '#fff8e1', border: `1px solid ${GOLD}`, borderRadius: 6, fontSize: 13, color: '#92400e' }}>
                You have unsaved changes. Click <strong>Save Changes</strong> to apply.
              </div>
            )}
          </>
        )}
      </div>

      {/* ── House Rules edit modal ── */}
      {editing && (
        <div style={S.modal} onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div style={S.modalBox}>
            <h3 style={{ margin: 0, fontSize: 18 }}>{editing._id ? 'Edit Rule' : 'New House Rule'}</h3>

            <label style={S.label}>Name *</label>
            <input style={S.input} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g., Require ASTM C920 in sealants" />

            <label style={S.label}>Description</label>
            <input style={S.input} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="What this rule checks and why it matters" />

            <label style={S.label}>Rule Type</label>
            <select style={S.input} value={editing.ruleType} onChange={(e) => setEditing({ ...editing, ruleType: e.target.value })}>
              {RULE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div style={S.help}>{RULE_TYPES.find(t => t.value === editing.ruleType)?.help}</div>

            <label style={S.label}>Target *</label>
            <input style={S.input} value={editing.target} onChange={(e) => setEditing({ ...editing, target: e.target.value })} placeholder="CSI section number" />
            <div style={S.help}>{targetHelp}</div>

            {/* Code-compliance specific fields */}
            {isCodeRule && (
              <div style={S.codeBox}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>🏛️ Code Compliance Details</div>
                <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>The rule fires when the target section is missing this code citation. At least one field below is required.</div>

                <label style={S.label}>Code Standard</label>
                <select style={S.input} value={editing.codeStandard} onChange={(e) => setEditing({ ...editing, codeStandard: e.target.value })}>
                  <option value="">— Select —</option>
                  {CODE_STANDARDS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>

                <label style={S.label}>Code Section / Article (optional)</label>
                <input style={S.input} value={editing.codeSection} onChange={(e) => setEditing({ ...editing, codeSection: e.target.value })} placeholder='e.g., "Section 140.4" or "110.26"' />
                <div style={S.help}>Specific section/article number within the standard.</div>

                <label style={S.label}>Compliance Phrase (optional)</label>
                <input style={S.input} value={editing.compliancePhrase} onChange={(e) => setEditing({ ...editing, compliancePhrase: e.target.value })} placeholder='e.g., "minimum efficiency" or "UL listed"' />
                <div style={S.help}>Key phrase that should appear alongside the citation. Use <code>|</code> to specify alternatives (e.g., <code>LPD|lighting power density</code>).</div>

                {livePattern && (
                  <>
                    <label style={S.label}>Compiled Pattern (preview)</label>
                    <div style={S.codePreview}>{livePattern}</div>
                  </>
                )}
              </div>
            )}

            {/* Generic pattern field for non-code rules */}
            {showsPattern && (
              <>
                <label style={S.label}>Pattern</label>
                <input style={S.input} value={editing.pattern} onChange={(e) => setEditing({ ...editing, pattern: e.target.value })} placeholder="Text or regex to look for" />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={editing.patternIsRegex} onChange={(e) => setEditing({ ...editing, patternIsRegex: e.target.checked })} />
                  Treat pattern as regular expression
                </label>
              </>
            )}

            <label style={S.label}>Severity</label>
            <select style={S.input} value={editing.severity} onChange={(e) => setEditing({ ...editing, severity: e.target.value })}>
              <option value="critical">🔴 Critical</option>
              <option value="warning">🟡 Warning</option>
              <option value="info">🔵 Info</option>
            </select>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <button style={S.btnSecondary} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Rule'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}